import { createContext, ReactNode } from 'react';
import { AnalyticsPixelIds } from 'core/analytics/entities';
import {
  AnalyticsService,
  AppnebulaAxonService,
  AppnebulaFacebookService,
  AppnebulaSnapchatService,
  AppnebulaTaboolaService,
  AppnebulaTikTokService,
  ArrivalService,
  FunnelStepService,
  GTMService,
  SnitcherService,
} from 'core/analytics/interfaces';
import { AuthService } from 'core/auth/services';
import { ObservabilitySystem } from 'core/common/observability/interfaces';
import { Logger } from 'core/common/services/logger/interfaces';
import { checkIsClientSide } from 'core/common/utils/checkIsClientSide';
import { ContentServiceInterface } from 'core/content/interfaces';
import { CookieConsentService } from 'core/cookie-consent/interfaces';
import { FeatureFlagsConfig } from 'core/feature-flags/entities';
import { FeatureFlags } from 'core/feature-flags/services';
import { FunnelService } from 'core/funnel/interfaces';
import { HandlebarsTemplateEngine } from 'core/funnel/services';
import { HumanDesignReportService } from 'core/human-design/services';
import { LocalizationService } from 'core/localization/services';
import { NatalChartServiceInterface } from 'core/natal-chart/interfaces';
import { OffersService } from 'core/offers/services';
import { PaymentService } from 'core/payments/services';
import { FaceDetectionService, PalmDetectionService } from 'core/scan/services';
import { SupportFormServiceInterface } from 'core/support/interfaces';
import {
  AutocompleteService,
  LocationService,
  SubscriptionsServiceInterface,
  UserReadingServiceInterface,
  UserServiceInterface,
  UtmTagsService,
} from 'core/user/services';
import { ZodiacService } from 'core/zodiac/services';
import {
  AppEnvironment,
  ChannelAttributionService,
  ContentManagerService,
  CookiesService,
  EmailManagerServiceInterface,
  HeatmapRecordingService,
  HttpClient,
  Meta,
} from '../services';
import { ErrorMonitoring } from '../services/errorMonitoring';
import { FunnelSettingsServiceInterface } from '../services/funnel-settings';
import { StorePersistenceInterface } from '../store/services';
import { Config } from './ConfigContext';
import { initClientDependencies } from './initClientDependencies';
import { initServerDependencies } from './initServerDependencies';

export interface Services {
  appEnvironment: AppEnvironment;
  utmTagsService: UtmTagsService;
  zodiacService: ZodiacService;
  cookiesService: CookiesService;
  loggingService: Logger;
  analyticsService: AnalyticsService;
  locationService: LocationService;
  subscriptionService: SubscriptionsServiceInterface;
  authService: AuthService;
  userService: UserServiceInterface;
  userReadingService: UserReadingServiceInterface;
  natalChartService: NatalChartServiceInterface;
  contentService: ContentServiceInterface;
  emailManagerService: EmailManagerServiceInterface;
  autocompleteService: AutocompleteService;
  offersService: OffersService;
  palmDetectionService: PalmDetectionService;
  faceDetectionService: FaceDetectionService;
  templateEngine: HandlebarsTemplateEngine;
  cookieConsentService: CookieConsentService;
  funnelService: FunnelService;
  humanDesignReportService: HumanDesignReportService;
  observabilitySystem: ObservabilitySystem;
  heatmapRecordingService: HeatmapRecordingService;
  storePersistenceService: StorePersistenceInterface;
  errorMonitoringService: ErrorMonitoring;
  metaService: Meta;
  channelAttributionService: ChannelAttributionService;
  tikTokService: AppnebulaTikTokService;
  taboolaService: AppnebulaTaboolaService;
  axonService: AppnebulaAxonService;
  snapchatService: AppnebulaSnapchatService;
  gtmService: GTMService;
  funnelSettingsService: FunnelSettingsServiceInterface;
  funnelStepService: FunnelStepService;
  facebookService: AppnebulaFacebookService;
  localizationService: LocalizationService;
  httpClient: HttpClient;
  snitcherService: SnitcherService;
  arrivalService: ArrivalService;
  contentManagerService: ContentManagerService;
  paymentService: PaymentService;
  featureFlags: FeatureFlags;
  supportFormService: SupportFormServiceInterface;
}

export const initDependencies = (
  config: Config,
  options: {
    analyticsPixelIds?: AnalyticsPixelIds;
    features?: FeatureFlagsConfig['features'];
  },
): Services => {
  const appDependencies = checkIsClientSide()
    ? initClientDependencies(config, options)
    : initServerDependencies(config, options);

  const { featureFlags, analyticsService, heatmapRecordingService } = appDependencies;

  featureFlags.onTrackExperiment((experiment, result) => {
    const variationId = String(result.variationId);

    analyticsService.setUserExperimentGroupName(experiment.key, variationId);
    analyticsService.trackEvent('ab_test_split', {
      test_name: experiment.key,
      test_group: variationId,
    });
    heatmapRecordingService.addCustomTag(experiment.key, variationId);
  });

  return appDependencies;
};

export const ServicesContext = createContext<Services>(undefined!);

type ServicesProviderProps = {
  value: Services;
  children: ReactNode;
};

export const ServicesProvider = ({ value, children }: ServicesProviderProps) => {
  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
};
